import React, { useContext, useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { ChoiceGroup, TextField } from '@fluentui/react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { GoalCreationWizardContext } from '../goal-creation-wizard-context';
import { getStepStyles } from '../../../conversion-wizard-components/src/shared-styles/shared-content-style';
import { getAppPlatformOptions } from './goal-creation-options';
import { validateString } from '../shared/validation-util';

const AppInstallSettingUnstyled = (props) => {
  const {
    classes,
    updateGoalProps,
    showError,
    resetShowError,
  } = props;
  const i18n = useContext(I18nContext);
  const wizardContext = useContext(GoalCreationWizardContext);
  const [appPlatform, setAppPlatform] = useState(wizardContext.goalContext.ApplicationPlatform);
  const [appId, setAppId] = useState(wizardContext.goalContext.ApplicationStoreId);
  const [appIdErrorMessage, setAppIdErrorMessage] = useState('');

  const title = useMemo(() => (appPlatform === 'Android' ? i18n.getString(_TL_('Package Name')) : i18n.getString(_TL_('App ID'))), [appPlatform, i18n]);
  const appPlatformOptions = useMemo(() => getAppPlatformOptions(i18n), [i18n]);

  const appIdPlaceholders = useMemo(() => {
    const map = {};
    _.each(appPlatformOptions, (item) => {
      map[item.key] = item.placeholder;
    });
    return map;
  }, [appPlatformOptions]);

  const getAppIdErrorMessage = useCallback((value) => {
    if (_.isEmpty(value) || !validateString(value)) {
      return i18n.getString(_TL_('Enter a valid app ID or package name.'));
    }
    return '';
  }, [i18n]);

  useEffect(() => {
    if (showError) {
      setAppIdErrorMessage(getAppIdErrorMessage(appId));
      resetShowError();
    }
  }, [appId, getAppIdErrorMessage, resetShowError, showError]);

  const onChangeAppPlatformSelection = useCallback((event, option) => {
    setAppPlatform(option.key);
    updateGoalProps({ ApplicationPlatform: option.key });
  }, [updateGoalProps]);

  const onChangeAppId = useCallback((event, value) => {
    setAppId(value);
    setAppIdErrorMessage(getAppIdErrorMessage(value));
    updateGoalProps({ ApplicationStoreId: _.isEmpty(appIdErrorMessage) ? value : undefined });
  }, [appIdErrorMessage, getAppIdErrorMessage, updateGoalProps]);

  return (
    <div className="app-install-setting">
      <div>
        <span className={classes.nonAccordionTitle}>{i18n.getString(_TL_('App platform'))}</span>
        <div className={classes.rowContainer}>
          <ChoiceGroup
            selectedKey={appPlatform}
            options={appPlatformOptions}
            onChange={onChangeAppPlatformSelection}
          />
        </div>
      </div>
      <div>
        <span className={classes.nonAccordionTitle}>{title}</span>
        <div className={classes.rowContainer}>
          <TextField
            value={appId}
            placeholder={appIdPlaceholders[appPlatform]}
            onChange={onChangeAppId}
            styles={{ fieldGroup: { width: 180 } }}
          />
        </div>
        {!_.isEmpty(appIdErrorMessage) && (
          <div className="row" role="alert">
            <p className={classes.errorText}>{appIdErrorMessage}</p>
          </div>
        )}
      </div>
    </div>
  );
};

AppInstallSettingUnstyled.propTypes = {
  classes: PropTypes.shape(_.mapObject(
    getStepStyles(),
    _.constant(PropTypes.string.isRequired)
  )).isRequired,
  updateGoalProps: PropTypes.func.isRequired,
  showError: PropTypes.bool.isRequired,
  resetShowError: PropTypes.func.isRequired,
};

export const AppInstallSetting = withDefaultStyles(AppInstallSettingUnstyled, getStepStyles);
